/**
 * The Enterprise control module
 */

import { API_URL, requestData } from "./utils";
import BillingModule from "./billing";
import { ServerGetResponse } from "../../../dto/ServerResponse";
import { ServerGetPayload } from "../../../dto/ServerRequest";
import Enterprise, { EnterpriseApiData } from "../../../dto/Enterprise";
import EnterpriseOverview from "../../../dto/EnterpriseOverview";
import Address from "../../../dto/Address";
import { UserFormValues } from "../../../dto/User";
import Entity from "../../../dto/Entity";

/*
TODO: These interfaces are right now made generic by adding `[key: string]: string | number | boolean | Object;`
to support all forms of payload. We need to add type def specific to that payload to for correct type checks.
*/

export interface SenderAddress {
  enterprise_id: string;
  entity_id?: string;
  country_code?: string;
  sender_id?: string;
  reason?: string | null;
  prevData?: {
    country_code: string;
    sender_address: string;
  };
  [key: string]: string | Object;
}
interface Supplier {
  supplier_id: string;
  [key: string]: string | number | boolean | Object;
}
interface User {
  user_id: string;
  [key: string]: string | number | boolean | Object;
}
interface Contact {
  contact_id: string;
  [key: string]: string | number | boolean | Object;
}
interface InvitationModule {
  create: (
    user: Object,
    enterprise_id: string,
    entity_id: string,
    primary_language: string,
  ) => Promise<any>;
  get: (user_invitation_id: string) => Promise<any>;
  get_matching_email: (email: string) => Promise<any>;
  getAll: (query: Object) => Promise<any>;
  accept: (
    user_invitation_id: string,
    enterprise_id: string,
    entity_id: string,
    password: string,
  ) => Promise<any>;
  delete: (user_invitation_id: string) => Promise<any>;
}
interface UserModule {
  add: (
    user?: UserFormValues,
    enterprise_id?: string,
    entity_id?: string,
  ) => Promise<any>;
  get: (
    user_id?: string,
    enterprise_id?: string,
    entity_id?: string,
  ) => Promise<any>;
  update: (user: User, enterprise_id: string, entity_id: string) => Promise<any>;
  delete: (
    user_id?: string,
    enterprise_id?: string,
    entity_id?: string,
  ) => Promise<any>;
  invitation: InvitationModule;
}

interface CustomLimitsModule {
  add: (config: any, enterprise_id?: string) => Promise<any>;
  get: (enterprise_id?: string) => Promise<any>;
  update: (config: any, enterprise_id: string) => Promise<any>;
}

interface EntityCustomLimitsModule {
  add: (config: any, enterprise_id?: string, entity_id?: string) => Promise<any>;
  get: (enterprise_id?: string, entity_id?: string) => Promise<any>;
  update: (config: any, enterprise_id: string, entity_id?: string) => Promise<any>;
}
interface AddressModule {
  add: (
    address: Omit<
      Address,
      | "enterprise_id"
      | "enterprise_id"
      | "address_id"
      | "entity_id"
      | "date_created"
      | "time_last_updated"
    >,
    enterprise_id: string,
    entity_id: string,
  ) => Promise<any>;
  get: (
    address_id: string,
    enterprise_id: string,
    entity_id: string,
  ) => Promise<any>;
  update: (
    address: Address,
    enterprise_id: string,
    entity_id: string,
  ) => Promise<any>;
  delete: (
    address_id: string,
    enterprise_id: string,
    entity_id: string,
  ) => Promise<any>;
}
interface ContactModule {
  add: (contact, enterpriseId, entityId) => Promise<any>;
  update: (contact, enterpriseId: string, entityId: string) => Promise<any>;
  get: (
    contact_id: string,
    enterprise_id: string,
    entity_id: string,
  ) => Promise<any>;
  delete: (
    contact_id: string,
    enterprise_id: string,
    entity_id: string,
  ) => Promise<any>;
}
interface BilateralAgreementModule {
  add: (
    supplier: Supplier,
    enterprise_id: string,
    entity_id: string,
  ) => Promise<any>;
  get: (
    supplier_id: string,
    enterprise_id: string,
    entity_id: string,
  ) => Promise<any>;
  update: (
    supplier: Supplier,
    enterprise_id: string,
    entity_id: string,
  ) => Promise<any>;
  delete: (
    supplier_id: string,
    enterprise_id: string,
    entity_id: string,
  ) => Promise<any>;
}
export interface EntityModule {
  add: (entity_data) => Promise<any>;
  get: (enterprise_id, entity_id) => Promise<any>;
  update: (entity_data, enterprise_id, entity_id) => Promise<any>;
  delete: (enterprise_id, entity_id) => Promise<any>;
  address: AddressModule;
  contact: ContactModule;
  whitelist: WhiteListModule;
  bilateral_agreement: BilateralAgreementModule;
  user: UserModule;
  billing: BillingModule;
  getOverview: (
    enterprise_id: any,
    entity_id: any,
    signal: any,
  ) => Promise<Entity | EnterpriseOverview>;
}

export interface WhiteListModule {
  add: (data: SenderAddress) => Promise<any>;
  update: ({
    enterprise_id,
    entity_id,
    country_code,
    sender_id,
    prevData,
  }: SenderAddress) => Promise<any>;
  delete: ({
    enterprise_id,
    entity_id,
    country_code,
    sender_id,
  }: SenderAddress) => Promise<any>;
  activate: ({
    enterprise_id,
    entity_id,
    country_code,
    sender_id,
    reason,
  }: SenderAddress) => Promise<any>;
  activateMultiple: (payload: SenderAddress[]) => Promise<any>;
  setWhiteListAsDefault: (data) => Promise<any>;
  unsuspend: ({
    enterprise_id,
    entity_id,
    country_code,
    sender_id,
    reason,
  }: SenderAddress) => Promise<any>;
  retire: ({
    enterprise_id,
    entity_id,
    country_code,
    sender_id,
    reason,
  }: SenderAddress) => Promise<any>;
  suspend: ({
    enterprise_id,
    entity_id,
    country_code,
    sender_id,
    reason,
  }: SenderAddress) => Promise<any>;
  get: (enterprise_id: string, entity_id: string, query: Object) => Promise<any>;
  getAll: (payload: {
    enterprise_id: string;
    entity_id?: string;
    page: number;
    pageSize: number;
  }) => Promise<any>;
  addSenderIDRestriction: (data: any) => Promise<any>;
  updateSenderIDRestriction: (data: any) => Promise<any>;
}
const whitelistSenderAddress = "/whitelisted_sender_address";
export default class EnterpriseModule {
  enterprises: any[];
  billing: BillingModule;
  address: AddressModule;
  whitelist: WhiteListModule;
  contact: ContactModule;
  bilateral_agreement: BilateralAgreementModule;
  user: UserModule;
  entity: EntityModule;
  customLimits: CustomLimitsModule;
  entityCustomLimits: EntityCustomLimitsModule;

  constructor() {
    this.enterprises = [];
    this.billing = new BillingModule();
    this.address = {
      add: (
        address: Omit<
          Address,
          | "enterprise_id"
          | "enterprise_id"
          | "address_id"
          | "entity_id"
          | "date_created"
          | "time_last_updated"
        >,
        enterprise_id: string,
        entity_id: string,
      ) => {
        const endpoint = entity_id
          ? `/enterprise_address/${enterprise_id}/${entity_id}`
          : `/enterprise_address/${enterprise_id}`;
        return requestData("POST", endpoint, { address });
      },
      get: (address_id: string, enterprise_id: string, entity_id: string) => {
        const endpoint = entity_id
          ? `/enterprise_address/${enterprise_id}/${entity_id}/${address_id}`
          : `/enterprise_address/${enterprise_id}/${address_id}`;
        return requestData("GET", endpoint);
      },
      update: (address: Address, enterprise_id: string, entity_id: string) => {
        // { "address": { ... address json object ... },
        // "version_datestamp": ".. time last modified datestamp of existing object that is being updated (taken from last GET on the object).." }
        const endpoint = entity_id
          ? `/enterprise_address/${enterprise_id}/${entity_id}/${address.address_id}`
          : `/enterprise_address/${enterprise_id}/${address.address_id}`;
        return requestData("PUT", endpoint, { address });
      },
      delete: (address_id: string, enterprise_id: string, entity_id: string) => {
        const endpoint = entity_id
          ? `/enterprise_address/${enterprise_id}/${entity_id}/${address_id}`
          : `/enterprise_address/${enterprise_id}/${address_id}`;
        return requestData("DELETE", endpoint);
      },
    };
    this.whitelist = {
      add: (data: SenderAddress) => {
        return requestData("POST", whitelistSenderAddress, data);
      },
      update: ({
        enterprise_id,
        entity_id = "",
        country_code,
        sender_id,
        prevData,
      }: SenderAddress) => {
        const endpoint = entity_id
          ? `/whitelisted_sender_address/${enterprise_id}/${entity_id}`
          : `/whitelisted_sender_address/${enterprise_id}`;
        return requestData(
          "PUT",
          `${endpoint}/${prevData.country_code}/${prevData.sender_address}`,
          { country_code, sender_id },
        );
      },
      get: (enterprise_id?: string, entity_id?: string, query?: Object) => {
        let endpoint = whitelistSenderAddress;
        if (enterprise_id) {
          endpoint += entity_id
            ? `/${enterprise_id}/${entity_id}`
            : `/${enterprise_id}`;
        }
        return requestData("GET", endpoint, query);
      },
      delete({ enterprise_id, entity_id, country_code, sender_id }: SenderAddress) {
        const endpoint = entity_id
          ? `/whitelisted_sender_address?enterprise_id=${enterprise_id}&entity_id=${entity_id}`
          : `/whitelisted_sender_address?enterprise_id=${enterprise_id}`;
        return requestData(
          "DELETE",
          `${endpoint}&country_code=${country_code}&sender_id=${encodeURIComponent(
            sender_id,
          )}`,
        );
      },
      suspend({
        enterprise_id,
        entity_id,
        country_code,
        sender_id,
        reason = null,
      }: SenderAddress) {
        const endpoint = entity_id
          ? `/whitelisted_sender_address_suspend?enterprise_id=${enterprise_id}&entity_id=${entity_id}`
          : `/whitelisted_sender_address_suspend?enterprise_id=${enterprise_id}`;
        return requestData(
          "POST",
          `${endpoint}&country_code=${country_code}&sender_id=${encodeURIComponent(
            sender_id,
          )}`,
          {
            reason,
          },
        );
      },
      unsuspend({
        enterprise_id,
        entity_id,
        country_code,
        sender_id,
        reason = null,
      }: SenderAddress) {
        const endpoint = entity_id
          ? `/whitelisted_sender_address_unsuspend?enterprise_id=${enterprise_id}&entity_id=${entity_id}`
          : `/whitelisted_sender_address_unsuspend?enterprise_id=${enterprise_id}`;
        return requestData(
          "POST",
          `${endpoint}&country_code=${country_code}&sender_id=${encodeURIComponent(
            sender_id,
          )}`,
          {
            reason,
          },
        );
      },
      retire({
        enterprise_id,
        entity_id,
        country_code,
        sender_id,
        reason = null,
      }: SenderAddress) {
        const endpoint = entity_id
          ? `/whitelisted_sender_address_retire?enterprise_id=${enterprise_id}&entity_id=${entity_id}`
          : `/whitelisted_sender_address_retire?enterprise_id=${enterprise_id}`;
        return requestData(
          "POST",
          `${endpoint}&country_code=${country_code}&sender_id=${encodeURIComponent(
            sender_id,
          )}`,
          {
            reason,
          },
        );
      },
      activate({
        enterprise_id,
        entity_id,
        country_code,
        sender_id,
        reason = null,
      }: SenderAddress) {
        const endpoint = entity_id
          ? `/whitelisted_sender_address_activate?enterprise_id=${enterprise_id}&entity_id=${entity_id}`
          : `/whitelisted_sender_address_activate?enterprise_id=${enterprise_id}`;
        return requestData(
          "POST",
          `${endpoint}&country_code=${country_code}&sender_id=${encodeURIComponent(
            sender_id,
          )}`,
          {
            reason,
          },
        );
      },
      activateMultiple(payload: SenderAddress[]) {
        return requestData("POST", "/whitelisted_sender_address_activate_multiple", {
          payload,
        });
      },
      setWhiteListAsDefault(senderAddress) {
        return requestData("POST", `/whitelisted-sender-as-default`, senderAddress);
      },
      getAll: async (payload) => {
        let endpoint = "/whitelisted_sender_address";
        endpoint +=
          payload.enterprise_id !== "all" ? `/${payload.enterprise_id}` : "";
        if (payload?.entity_id && payload?.entity_id !== "all")
          endpoint += `/${payload?.entity_id}`;
        const data = await requestData("GET", endpoint, payload);
        const list = data?._embedded?.whitelisted_sender_addresses;
        return {
          ...data,
          _embedded: {
            ...data._embedded,
            whitelisted_sender_addresses: list,
          },
        };
      },
      addSenderIDRestriction: (data) => {
        return requestData("POST", `/sender_id_restriction`, data);
      },
      updateSenderIDRestriction: (data) => {
        return requestData("PUT", `/sender_id_restriction`, data);
      },
    };
    this.contact = {
      add: (contact: Object, enterprise_id: string, entity_id: string) => {
        const endpoint = entity_id
          ? `/enterprise_contact/${enterprise_id}/${entity_id}`
          : `/enterprise_contact/${enterprise_id}`;
        return requestData("POST", endpoint, { contact });
      },
      get: (contact_id: string, enterprise_id: string, entity_id: string) => {
        const endpoint = entity_id
          ? `/enterprise_contact/${enterprise_id}/${entity_id}/${contact_id}`
          : `/enterprise_contact/${enterprise_id}/${contact_id}`;
        return requestData("GET", endpoint);
      },
      update: (contact: Contact, enterprise_id: string, entity_id: string) => {
        // { "contact": { ... contact json object ... },
        // "version_datestamp": ".. time last modified datestamp of existing object that is being updated (taken from last GET on the object).." }
        const endpoint = entity_id
          ? `/enterprise_contact/${enterprise_id}/${entity_id}/${contact.contact_id}`
          : `/enterprise_contact/${enterprise_id}/${contact.contact_id}`;
        return requestData("PUT", endpoint, { contact });
      },
      delete: (contact_id: string, enterprise_id: string, entity_id: string) => {
        const endpoint = entity_id
          ? `/enterprise_contact/${enterprise_id}/${entity_id}/${contact_id}`
          : `/enterprise_contact/${enterprise_id}/${contact_id}`;
        return requestData("DELETE", endpoint);
      },
    };
    this.bilateral_agreement = {
      add: (supplier: Supplier, enterprise_id: string, entity_id: string) => {
        const endpoint = entity_id
          ? `/enterprise_bilateral_agreement/${enterprise_id}/${entity_id}`
          : `/enterprise_bilateral_agreement/${enterprise_id}`;
        return requestData("POST", endpoint, { supplier });
      },
      get: (supplier_id: string, enterprise_id: string, entity_id: string) => {
        const endpoint = entity_id
          ? `/enterprise_bilateral_agreement/${enterprise_id}/${entity_id}/${supplier_id}`
          : `/enterprise_bilateral_agreement/${enterprise_id}/${supplier_id}`;
        return requestData("GET", endpoint);
      },
      update: (supplier: Supplier, enterprise_id: string, entity_id: string) => {
        // { "supplier": { ... supplier json object ... },
        // "version_datestamp": ".. time last modified datestamp of existing object that is being updated (taken from last GET on the object).." }
        const endpoint = entity_id
          ? `/enterprise_bilateral_agreement/${enterprise_id}/${entity_id}/${supplier.supplier_id}`
          : `/enterprise_bilateral_agreement/${enterprise_id}/${supplier.supplier_id}`;
        return requestData("PUT", endpoint, { supplier });
      },
      delete: (supplier_id: string, enterprise_id: string, entity_id: string) => {
        const endpoint = entity_id
          ? `/enterprise_bilateral_agreement/${enterprise_id}/${entity_id}/${supplier_id}`
          : `/enterprise_bilateral_agreement/${enterprise_id}/${supplier_id}`;
        return requestData("DELETE", endpoint);
      },
    };
    this.user = {
      add: (user: UserFormValues, enterprise_id: string, entity_id: string) => {
        const endpoint = entity_id
          ? `/enterprise_user/${enterprise_id}/${entity_id}`
          : `/enterprise_user/${enterprise_id}`;
        return requestData("POST", endpoint, { user });
      },
      get: (user_id: string, enterprise_id: string, entity_id: string) => {
        const endpoint = entity_id
          ? `/enterprise_user/${enterprise_id}/${entity_id}/${user_id}`
          : `/enterprise_user/${enterprise_id}/${user_id}`;
        return requestData("GET", endpoint);
      },
      update: (user: User, enterprise_id: string, entity_id: string) => {
        // { "user": { ... user json object ... },
        // "version_datestamp": ".. time last modified datestamp of existing object that is being updated (taken from last GET on the object).." }
        const endpoint = entity_id
          ? `/enterprise_user/${enterprise_id}/${entity_id}/${user.user_id}`
          : `/enterprise_user/${enterprise_id}/${user.user_id}`;
        return requestData("PUT", endpoint, { user });
      },
      delete: (user_id: string, enterprise_id: string, entity_id: string) => {
        const endpoint = entity_id
          ? `/enterprise_user/${enterprise_id}/${entity_id}/${user_id}`
          : `/enterprise_user/${enterprise_id}/${user_id}`;
        return requestData("DELETE", endpoint);
      },
      // ENTERPRISE USER INVITES
      invitation: {
        /**
         * Create a new Invitation for a user
         * @param {string} enterprise_id
         * @param {string} [entity_id]
         *
         * @returns {object} the created invitation
         */
        create: (
          user: Object,
          enterprise_id: string,
          entity_id: string,
          primary_language: string = "JAPANESE",
        ) => {
          const endpoint = entity_id
            ? `/enterprise_user_invitation/${enterprise_id}/${entity_id}`
            : `/enterprise_user_invitation/${enterprise_id}`;
          return requestData("POST", endpoint, { user, primary_language });
        },
        /**
         * Get an Invitation for a user
         * @param {string} user_invitation_id
         *
         * @returns {object} the user invitation
         */ get: (user_invitation_id: string) => {
          return requestData(
            "GET",
            `/enterprise_user_invitation/${user_invitation_id}`,
          );
        },

        /**
         * Get an Invitation for a user matching email
         * @param {string} email
         *
         * @returns {object} the user invitation
         */
        get_matching_email: (email: string) => {
          return requestData(
            "DELETE",
            `/enterprise_user_invitation_matching_email/${email}`,
          );
        },
        /**
         * Query invitations
         * @param {object} query
         *
         * @returns {object} the user invitation
         */
        getAll: (query: Object) => {
          //     ?page-size=&page=[&enterprise-id=][&entity-id=][&name-starts-with=][&name-contains=][&email-starts-with=][&email-contains=]
          return requestData("GET", `/query/user_invitations`, {
            ...query,
          });
        },
        /**
         * Accept an Invitation and create a user
         * @param {string} user_invitation_id
         * @param {string} password
         *
         */
        accept: (
          user_invitation_id: string,
          enterprise_id: string,
          entity_id: string,
          password: string,
        ) => {
          return requestData(
            "POST",
            `/accept_enterprise_user_invitation/${user_invitation_id}`,
            { new_password: password, enterprise_id, entity_id },
          );
        },
        /**
         * Delete an Invitation for a user
         * @param {string} user_invitation_id
         *
         */ delete: (user_invitation_id: string) => {
          return requestData(
            "DELETE",
            `/enterprise_user_invitation/${user_invitation_id}`,
          );
        },
      },
    };
    this.entity = {
      add: (entity_data) => {
        return requestData(
          "POST",
          `/enterprise_entity/${entity_data.enterprise_id}`,
          {
            entity: entity_data.entity,
          },
        );
      },
      get: (enterprise_id, entity_id) => {
        return requestData(
          "GET",
          `/enterprise_entity/${enterprise_id}/${entity_id}`,
        );
      },
      getOverview: this.getOverview,
      update: (entity_data, enterprise_id, entity_id) => {
        return requestData(
          "PUT",
          `/enterprise_entity/${enterprise_id}/${entity_id}`,
          entity_data,
        );
      },
      delete: this.delete,

      address: this.address,
      contact: this.contact,
      whitelist: this.whitelist,
      bilateral_agreement: this.bilateral_agreement,
      user: this.user,
      billing: this.billing,
    };

    this.customLimits = {
      add: (config, enterprise_id) => {
        return requestData(
          "POST",
          `/enterprise_custom_limits/${enterprise_id}`,
          config,
        );
      },
      get: (enterprise_id) => {
        return requestData("GET", `/enterprise_custom_limits/${enterprise_id}`);
      },
      update: (config, enterprise_id) => {
        return requestData(
          "PUT",
          `/enterprise_custom_limits/${enterprise_id}`,
          config,
        );
      },
    };

    this.entityCustomLimits = {
      add: (config, enterprise_id, entity_id) => {
        return requestData(
          "POST",
          `/enterprise_entity_custom_limits/${enterprise_id}/${entity_id}`,
          config,
        );
      },
      get: (enterprise_id, entity_id) => {
        return requestData(
          "GET",
          `/enterprise_entity_custom_limits/${enterprise_id}/${entity_id}`,
        );
      },
      update: (config, enterprise_id, entity_id) => {
        return requestData(
          "PUT",
          `/enterprise_entity_custom_limits/${enterprise_id}/${entity_id}`,
          config,
        );
      },
    };
  }

  getCustomConfigLimit() {
    return requestData("GET", "/custom/config/limits");
  }

  async add(enterprise_data) {
    const enterprise = await requestData(
      "POST",
      `/signup_enterprise`,
      enterprise_data,
    );
    this.enterprises.push(enterprise);
    return Promise.resolve(enterprise);
  }
  delete(enterprise_id, entity_id) {
    const endpoint = entity_id
      ? `/enterprise_entity/${enterprise_id}/${entity_id}`
      : `/enterprise/${enterprise_id}`;
    return requestData("DELETE", endpoint);
  }
  getOverview(enterprise_id: string, entity_id: string, signal?: any) {
    const endpoint = entity_id
      ? `/enterprise_entity_overview/${enterprise_id}/${entity_id}`
      : `/enterprise_overview/${enterprise_id}`;
    return entity_id
      ? requestData<Entity>("GET", endpoint, "", "", signal)
      : requestData<EnterpriseOverview>("GET", endpoint, "", "", signal);
  }

  getQuery() {
    return requestData("GET", `/query/enterprise`);
  }

  requestQueryLink(
    query: ServerGetPayload,
    link: string = "/query/enterprise",
    abortSignal: AbortSignal,
  ) {
    const isAdmin = import.meta.env.VITE_MODE === "admin";
    if (isAdmin)
      return requestData<ServerGetResponse<EnterpriseApiData>, ServerGetPayload>(
        "GET",
        link,
        query,
        undefined,
        abortSignal,
      );
  }
  get(enterprise_id: string) {
    return requestData<Enterprise>("GET", `/enterprise/${enterprise_id}`);
  }
  getEnterprisesCsvURL(
    query,
    accountType,
    link: string = `/download/csv/enterprises/${accountType}`,
  ) {
    return API_URL + link + "?" + new URLSearchParams(query);
  }

  getResellerEnterprisesCsv(
    query,
    resellerId,
    link: string = `/download/csv/reseller/enterprises/${resellerId}`,
  ) {
    return API_URL + link + "?" + new URLSearchParams(query);
  }

  getSenderIDCsv(
    query,
    enterpriseId,
    link: string = `/download/csv/senderid/${enterpriseId}`,
  ) {
    return API_URL + link + "?" + new URLSearchParams(query);
  }

  update(enterprise_data: Enterprise) {
    // let enterprise_datta = enterprise_data;
    //{ "enterprise": { ... enterprise json object ... },
    // "version_datestamp": ".. time last modified datestamp of existing object that is being updated (taken from last GET on the object).." }
    return requestData("PUT", `/enterprise/${enterprise_data.enterprise_id}`, {
      enterprise: enterprise_data,
    });
  }
  updateAllowedSourceIps(enterprise_id: string, values: any) {
    // let enterprise_datta = enterprise_data;
    //{ "enterprise": { ... enterprise json object ... },
    // "version_datestamp": ".. time last modified datestamp of existing object that is being updated (taken from last GET on the object).." }
    return requestData("PUT", `/enterprise/${enterprise_id}`, {
      enterprise: {
        allowed_source_ips: values?.allowedIps,
        time_last_updated: values?.time_last_updated,
      },
    });
  }
  activate(enterprise_id) {
    return requestData("POST", `/enterprise_activate/${enterprise_id}`);
  }
  suspend(enterprise_id, reason) {
    return requestData("POST", `/enterprise_suspend/${enterprise_id}`, {
      reason,
    });
  }
  unsuspend(enterprise_id) {
    return requestData("POST", `/enterprise_unsuspend/${enterprise_id}`);
  }
  retire(enterprise_id, reason) {
    return requestData("POST", `/enterprise_retire/${enterprise_id}`, {
      reason,
    });
  }

  fetchByStatus(query: Object) {
    return requestData("GET", `/query/enterprise`, {
      page: query["page"],
      "page-size": query["page-size"],
      "sort-key": query["sort-key"],
      "sort-order": query["sort-order"],
      "enterprise-status": query["enterprise_status"],
      "account-type": query["account_type"],
    });
  }

  fetchByAccountType(query: Object) {
    return requestData("GET", `/query/enterprise`, {
      page: query["page"],
      "page-size": query["page-size"],
      "sort-key": query["sort-key"],
      "sort-order": query["sort-order"],
      "enterprise-filter": query["enterprise_filter"],
    });
  }

  getEnterpriseSearchFilter() {
    return requestData("GET", `/query/enterprise/type`);
  }

  fetchResellerEnterpriseList(query: Object) {
    return requestData("GET", `/query/enterprise/reseller`, {
      page: query["page"],
      "page-size": query["page-size"],
      "sort-key": query["sort-key"],
      "sort-order": query["sort-order"],
      "reseller-id": query["reseller-id"],
    });
  }

  trialLimit = {
    get: (enterprise_id, entity_id = "") => {
      const endpoint = entity_id
        ? `/enterprise_trial_limit/${enterprise_id}/${entity_id}`
        : `/enterprise_trial_limit/${enterprise_id}`;
      return requestData("GET", endpoint);
    },
    update: (data, enterprise_id, entity_id = "") => {
      const endpoint = entity_id
        ? `/enterprise_trial_limit/${enterprise_id}/${entity_id}`
        : `/enterprise_trial_limit/${enterprise_id}`;
      return requestData("PUT", endpoint, data);
    },
    remove: (enterprise_id, entity_id = "") => {
      const endpoint = entity_id
        ? `/enterprise_trial_limit/${enterprise_id}/${entity_id}`
        : `/enterprise_trial_limit/${enterprise_id}`;
      return requestData("DELETE", endpoint);
    },
  };
}
